import { SessionOptions } from "iron-session";
import { ResponseCookie } from "next/dist/compiled/@edge-runtime/cookies";
import { UserRoleActions } from "../bigcommerce-b2b/types";
import { StoreRegion } from "@/lib/_config/stores";

export interface SessionMetafieldData {
	entityId: number;
	value: string;
}
export interface SessionUserData {
	bcCustomerId: number;
	b3UserId: number;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	companyRoleId: number;
	companyRoleName: string;
	metafields: {
		[key: string]: Record<string, SessionMetafieldData>;
	};
}

export interface SessionCompanyData {
	id: number;
	name: string;
	customerGroupId: number;
	status: number;
	extraFields: {
		fieldName: string;
		fieldValue: string;
	}[];
	currencyCode: string;
	adminUserId: number | undefined;
}

export interface SessionStoreData {
	region: StoreRegion;
	hash: string;
}

export interface SessionTokenData {
	b2bToken: {
		value: string;
		expiresAt: string;
	};
	customerAccessToken: {
		value: string;
		expiresAt: string;
	};
}

export const CURRENT_SESSION_VERSION = "2025.07.15";

export interface SessionData {
	version: string;
	rememberMe: boolean;
	store: SessionStoreData;
	token: SessionTokenData;
	user: SessionUserData;
	company: SessionCompanyData;
	permissions?: UserRoleActions;
}

export type ClientSessionData = Omit<
	SessionData,
	"version" | "rememberMe" | "token"
>;

export const convertToClientSession = (
	session: SessionData | undefined,
): SessionData | undefined => {
	if (!session) {
		return undefined;
	}

	const copySession = structuredClone(session);

	const { version, rememberMe, token, ...clientSession } = copySession;

	return clientSession as SessionData;
};
// 	store: "global",
// 	storeHash: "",
// 	customerId: 0,
// 	customerGroupId: 0,
// 	userId: 0,
// 	firstName: "",
// 	lastName: "",
// 	email: "",
// 	companyRoleId: -1,
// 	role: B3Role.DEFAULT,
// 	rolePermissions: {} as UserRoleActions,
// 	user: {},
// 	token: "",
// 	currencyCode: "",
// 	storefrontLoginToken: "",
// 	phoneNumber: "",
// 	companyRoleName: "",
// 	havePurchaseOrderNumber: "",
// 	companyId: undefined,
// };

export const SESSION_COOKIE_NAME = "__authSession";
export const SESSION_SEAL_PASSWORD =
	process.env.IRON_SESSION_PASSWORD ||
	"X44gMcgpwEmc24a6o&SySHFV7HN&E!!HFK%dqYFgEP";

export const sessionOptions: SessionOptions = {
	password: SESSION_SEAL_PASSWORD,
	cookieName: SESSION_COOKIE_NAME,
};

const getTTL = (rememberMe: boolean, isSuperAdmin: boolean = false) => {
	let days = rememberMe ? 30 : 7;

	// check if super admin and set to one day
	days = isSuperAdmin ? 1 : days;

	return days * 24 * 60 * 60;
};

export const getSessionOptions = (
	rememberMe: boolean,
	isSuperAdmin: boolean,
): SessionOptions => {
	const ttl = getTTL(rememberMe, isSuperAdmin);

	return {
		...sessionOptions,
		ttl,
		cookieOptions: {
			// secure only works in `https` environments
			// if your localhost is not on `https`, then use: `secure: process.env.NODE_ENV === "production"`
			secure: process.env.NODE_ENV === "production",
			//expire cookie 60 seconds before the session
			ttl: ttl - 60,
		},
	};
};

export const getSessionCookieOptions = (
	rememberMe: boolean,
	isSuperAdmin: boolean,
): Partial<ResponseCookie> => {
	const ttl = getTTL(rememberMe, isSuperAdmin);

	const maxAge = ttl - 60;

	return {
		httpOnly: true,
		sameSite: "lax",
		secure: process.env.NODE_ENV === "production",
		path: "/",
		maxAge,
		expires: new Date(Date.now() + maxAge * 1000),
	};
};

export const getB2BTokenExpiresAt = () => {
	// 1 day from now
	return new Date(Date.now() + 1000 * 60 * 60 * 24);
};
